'use client';

import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WebsiteButton, {
  WEBSITE_BUTTON_SX,
  WEBSITE_SECONDARY_BUTTON_SX,
} from '@/components/website/WebsiteButton';
import { WEBSITE_PAGE_BACKGROUND, WEBSITE_PAGE_BACKGROUND_DARK } from '@/consts/website-theme';
import { canSubmitRequiredForm } from '@/utils/required-fields';
import {
  createAppointment,
  fetchAppointmentTypes,
  fetchAvailability,
  fetchDesigners,
} from './booking-api';
import BookingAvailabilityGrid from './BookingAvailabilityGrid';
import { defaultAvailabilityRange, formatSlotDateTime } from './booking-utils';

const STEPS = {
  TYPE: 'type',
  DESIGNER: 'designer',
  DATETIME: 'datetime',
  INFO: 'info',
  SUCCESS: 'success',
};

const ANY_AVAILABLE = { id: 'any', displayName: 'Any Available' };

const REQUIRED_INFO_KEYS = ['firstName', 'lastName', 'phone', 'email'];

const INITIAL_INFO = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
};

/** Beige-integrated fields — matches footer newsletter, not stark white. */
const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: WEBSITE_PAGE_BACKGROUND,
    '& fieldset': {
      borderColor: WEBSITE_PAGE_BACKGROUND_DARK,
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.38)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0a0a0a',
    },
  },
  // Kill MUI primary blue on floating labels when focused.
  '& .MuiInputLabel-root': {
    color: 'rgba(0, 0, 0, 0.6)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#0a0a0a',
  },
};

/** Option rows sit on page beige — no floating white cards. */
const OPTION_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  width: '100%',
  textAlign: 'left',
  border: `1px solid ${WEBSITE_PAGE_BACKGROUND_DARK}`,
  borderRadius: 0,
  bgcolor: 'transparent',
  color: '#0a0a0a',
  px: 2.5,
  py: 2,
  cursor: 'pointer',
  font: 'inherit',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
  '&:hover': {
    borderColor: '#0a0a0a',
    bgcolor: 'rgba(0,0,0,0.03)',
  },
};

function StepHeading({ children, stepLabel }) {
  return (
    <Box sx={{ mb: 3 }}>
      {stepLabel ? (
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            mb: 1,
          }}
        >
          {stepLabel}
        </Typography>
      ) : null}
      <Typography component="h2" variant="h5" sx={{ fontWeight: 500 }}>
        {children}
      </Typography>
    </Box>
  );
}

StepHeading.propTypes = {
  children: PropTypes.node.isRequired,
  stepLabel: PropTypes.string,
};

export default function BookingBooker() {
  const [step, setStep] = React.useState(STEPS.TYPE);
  const [types, setTypes] = React.useState([]);
  const [designers, setDesigners] = React.useState([]);
  const [bootLoading, setBootLoading] = React.useState(true);
  const [bootError, setBootError] = React.useState('');

  const [appointmentType, setAppointmentType] = React.useState(null);
  const [designer, setDesigner] = React.useState(null);
  const [slot, setSlot] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState('');

  const [availability, setAvailability] = React.useState({ timezone: '', days: [] });
  const [availLoading, setAvailLoading] = React.useState(false);
  const [availError, setAvailError] = React.useState('');

  const [info, setInfo] = React.useState(INITIAL_INFO);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [created, setCreated] = React.useState(null);

  const [snack, setSnack] = React.useState({ open: false, message: '', severity: 'error' });

  const openErrorSnack = React.useCallback(message => {
    setSnack({ open: true, message: message || 'Something went wrong.', severity: 'error' });
  }, []);

  const closeSnack = React.useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setSnack(prev => ({ ...prev, open: false }));
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setBootLoading(true);
      setBootError('');
      try {
        const [typeList, designerList] = await Promise.all([
          fetchAppointmentTypes(),
          fetchDesigners(),
        ]);
        if (cancelled) return;
        setTypes(Array.isArray(typeList) ? typeList : []);
        setDesigners(Array.isArray(designerList) ? designerList : []);
      } catch (err) {
        if (!cancelled) setBootError(err.message || 'Unable to load booking options.');
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (step !== STEPS.DATETIME || !appointmentType) return undefined;
    let cancelled = false;
    const range = defaultAvailabilityRange();
    (async () => {
      setAvailLoading(true);
      setAvailError('');
      try {
        const data = await fetchAvailability({
          appointmentTypeId: appointmentType.id,
          designerId: designer?.id === 'any' || !designer ? null : designer.id,
          from: range.from,
          to: range.to,
        });
        if (cancelled) return;
        const days = Array.isArray(data?.days) ? data.days : [];
        setAvailability({ timezone: data?.timezone || '', days });
        setSelectedDate(prev => {
          if (prev && days.some(d => d.date === prev)) return prev;
          return days[0]?.date || '';
        });
      } catch (err) {
        if (!cancelled) {
          setAvailability({ timezone: '', days: [] });
          setAvailError(err.message || 'Unable to load availability.');
        }
      } finally {
        if (!cancelled) setAvailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, appointmentType, designer]);

  const canSubmitInfo = React.useMemo(
    () => canSubmitRequiredForm(info, REQUIRED_INFO_KEYS, ['email']),
    [info],
  );

  const handleSelectType = type => {
    setAppointmentType(type);
    setDesigner(null);
    setSlot(null);
    setSelectedDate('');
    setStep(STEPS.DESIGNER);
  };

  const clearType = () => {
    setAppointmentType(null);
    setDesigner(null);
    setSlot(null);
    setSelectedDate('');
    setStep(STEPS.TYPE);
  };

  const handleSelectDesigner = nextDesigner => {
    setDesigner(nextDesigner);
    setSlot(null);
    setSelectedDate('');
    setStep(STEPS.DATETIME);
  };

  const handleSelectSlot = nextSlot => {
    setSlot(nextSlot);
    setStep(STEPS.INFO);
  };

  const handleInfoChange = e => {
    const { name, value } = e.target;
    setInfo(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleConfirm = async e => {
    e.preventDefault();
    if (!appointmentType || !slot || !canSubmitInfo) return;
    setIsSubmitting(true);
    setFieldErrors({});
    try {
      const result = await createAppointment({
        appointmentTypeId: appointmentType.id,
        designerId: designer?.id === 'any' || !designer ? null : designer.id,
        startsAt: slot.startsAt,
        firstName: info.firstName.trim(),
        lastName: info.lastName.trim(),
        phone: info.phone.trim(),
        email: info.email.trim(),
      });
      setCreated(result);
      setStep(STEPS.SUCCESS);
    } catch (err) {
      if (err.fieldErrors && Object.keys(err.fieldErrors).length) {
        setFieldErrors(err.fieldErrors);
      }
      openErrorSnack(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (bootLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={36} sx={{ color: '#0a0a0a' }} />
      </Box>
    );
  }

  if (bootError) {
    return <Alert severity="error">{bootError}</Alert>;
  }

  return (
    <Box>
      {appointmentType && step !== STEPS.SUCCESS ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Selected:</Typography>
          <Chip
            label={`${appointmentType.name}${
              appointmentType.durationMinutes ? ` · ${appointmentType.durationMinutes} min` : ''
            }`}
            onDelete={clearType}
            deleteIcon={<CloseIcon />}
            sx={{
              borderRadius: 0,
              bgcolor: 'rgba(0,0,0,0.06)',
              fontWeight: 500,
              '& .MuiChip-deleteIcon': { color: '#0a0a0a' },
            }}
          />
          {designer ? (
            <Chip
              label={designer.displayName}
              sx={{ borderRadius: 0, bgcolor: 'transparent', border: '1px solid rgba(0,0,0,0.16)' }}
            />
          ) : null}
        </Box>
      ) : null}

      {step === STEPS.TYPE ? (
        <Box>
          <StepHeading stepLabel="Step 1 · Book">Select an appointment type</StepHeading>
          <Stack spacing={1.5}>
            {types.map(type => (
              <Box
                key={type.id}
                component="button"
                type="button"
                onClick={() => handleSelectType(type)}
                sx={OPTION_ROW_SX}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 500 }}>{type.name}</Typography>
                  {type.durationMinutes ? (
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
                      {type.durationMinutes} minutes
                    </Typography>
                  ) : null}
                </Box>
                <Box component="span" sx={WEBSITE_BUTTON_SX}>
                  Select
                </Box>
              </Box>
            ))}
            {!types.length ? (
              <Typography color="text.secondary">No appointment types are available.</Typography>
            ) : null}
          </Stack>
        </Box>
      ) : null}

      {step === STEPS.DESIGNER ? (
        <Box>
          <StepHeading stepLabel="Step 2 · Select">Choose a calendar</StepHeading>
          <Stack spacing={1.5}>
            {[ANY_AVAILABLE, ...designers].map(item => (
              <Box
                key={item.id}
                component="button"
                type="button"
                onClick={() => handleSelectDesigner(item)}
                sx={OPTION_ROW_SX}
              >
                <Typography sx={{ fontWeight: 500, minWidth: 0, flex: 1 }}>
                  {item.displayName}
                </Typography>
                <Box component="span" sx={WEBSITE_BUTTON_SX}>
                  Select
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}

      {step === STEPS.DATETIME ? (
        <Box>
          <StepHeading stepLabel="Step 3 · Date & time">Pick a time</StepHeading>
          <BookingAvailabilityGrid
            days={availability.days}
            timezone={availability.timezone}
            selectedDate={selectedDate}
            selectedStartsAt={slot?.startsAt || ''}
            loading={availLoading}
            error={availError}
            onSelectDate={date => {
              setSelectedDate(date);
              setSlot(null);
            }}
            onSelectSlot={handleSelectSlot}
          />
          <Box sx={{ mt: 3 }}>
            <WebsiteButton
              type="button"
              onClick={() => {
                setSlot(null);
                setStep(STEPS.DESIGNER);
              }}
              sx={WEBSITE_SECONDARY_BUTTON_SX}
            >
              Back
            </WebsiteButton>
          </Box>
        </Box>
      ) : null}

      {step === STEPS.INFO ? (
        <Box component="form" onSubmit={handleConfirm} noValidate>
          <StepHeading stepLabel="Step 4 · Your information">Confirm your details</StepHeading>
          {slot ? (
            <Typography sx={{ mb: 3, color: 'text.secondary' }}>
              {formatSlotDateTime(slot.startsAt, availability.timezone)}
              {designer ? ` · ${designer.displayName}` : ''}
            </Typography>
          ) : null}
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                name="firstName"
                label="First name"
                required
                fullWidth
                value={info.firstName}
                onChange={handleInfoChange}
                error={Boolean(fieldErrors.firstName)}
                helperText={fieldErrors.firstName || ''}
                sx={FIELD_SX}
              />
              <TextField
                name="lastName"
                label="Last name"
                required
                fullWidth
                value={info.lastName}
                onChange={handleInfoChange}
                error={Boolean(fieldErrors.lastName)}
                helperText={fieldErrors.lastName || ''}
                sx={FIELD_SX}
              />
            </Stack>
            <TextField
              name="phone"
              label="Phone"
              required
              fullWidth
              value={info.phone}
              onChange={handleInfoChange}
              error={Boolean(fieldErrors.phone)}
              helperText={fieldErrors.phone || ''}
              sx={FIELD_SX}
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              required
              fullWidth
              value={info.email}
              onChange={handleInfoChange}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email || ''}
              sx={FIELD_SX}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
            <WebsiteButton type="submit" disabled={!canSubmitInfo || isSubmitting}>
              {isSubmitting ? 'Submitting…' : 'Confirm Appointment'}
            </WebsiteButton>
            <WebsiteButton
              type="button"
              onClick={() => setStep(STEPS.DATETIME)}
              disabled={isSubmitting}
              sx={WEBSITE_SECONDARY_BUTTON_SX}
            >
              Back
            </WebsiteButton>
          </Stack>
        </Box>
      ) : null}

      {step === STEPS.SUCCESS ? (
        <Box sx={{ textAlign: 'center', py: { xs: 2, md: 4 } }}>
          <Alert severity="success" sx={{ borderRadius: 0, mb: 3, textAlign: 'left' }}>
            Your booking request has been received. We&apos;ll confirm shortly.
          </Alert>
          <Typography sx={{ color: 'text.secondary', mb: 1 }}>
            {created?.appointmentType?.name || appointmentType?.name}
          </Typography>
          {created?.startsAt ? (
            <Typography sx={{ fontWeight: 500, mb: 3 }}>
              {formatSlotDateTime(created.startsAt, availability.timezone)}
            </Typography>
          ) : null}
          <WebsiteButton
            type="button"
            onClick={() => {
              setStep(STEPS.TYPE);
              setAppointmentType(null);
              setDesigner(null);
              setSlot(null);
              setSelectedDate('');
              setInfo(INITIAL_INFO);
              setCreated(null);
            }}
          >
            Book another
          </WebsiteButton>
        </Box>
      ) : null}

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
